import React, { useState } from 'react';
import {
  Typography,
  List,
  Button,
  Form,
  Input,
  Space,
  Row,
  Col,
  Collapse,
} from 'antd';
import {
  PlusOutlined,
  BookOutlined,
  EditOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  HomeOutlined,
  CarOutlined,
  NotificationOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { Guideline, GuidelineCategory, GuidelineRecord } from '@/types/community/guideline';
import GuidelineModal from '@/components/features/community/GuidelineModal';
import { guidelines as mockGuidelines } from '@/mock/en/community/guideline';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

export default function CommunityGuidelines() {
  const { t } = useTranslation(['guidelines', 'common']);

  const [guidelineForm] = Form.useForm();

  const CATEGORY_MAP: { [key in GuidelineCategory]: { name: string; description: string; icon: React.ReactNode } } = {
    general_conduct: {
      name: t('guidelines:categories.general_conduct', 'General Conduct'),
      description: t('guidelines:categories.general_conduct_desc', 'Basic rules for all residents and visitors.'),
      icon: <TeamOutlined />,
    },
    amenity_usage: {
      name: t('guidelines:categories.amenity_usage', 'Amenity Usage'),
      description: t('guidelines:categories.amenity_usage_desc', 'Rules for using common facilities like pool, gym, clubhouse.'),
      icon: <HomeOutlined />,
    },
    parking_vehicles: {
      name: t('guidelines:categories.parking_vehicles', 'Parking & Vehicles'),
      description: t('guidelines:categories.parking_vehicles_desc', 'Guidelines for vehicle parking and movement within the premises.'),
      icon: <CarOutlined />,
    },
    safety_security: {
      name: t('guidelines:categories.safety_security', 'Safety & Security'),
      description: t('guidelines:categories.safety_security_desc', 'Important safety protocols and security measures.'),
      icon: <SafetyCertificateOutlined />,
    },
    announcements_notifications: {
      name: t('guidelines:categories.announcements_notifications', 'Announcements & Notifications'),
      description: t('guidelines:categories.announcements_notifications_desc', 'How official communications are handled.'),
      icon: <NotificationOutlined />,
    },
    common_expenses: {
      name: t('guidelines:categories.common_expenses', 'Common Expenses'),
      description: t('guidelines:categories.common_expenses_desc', 'Guidelines for shared expenses and billing.'),
      icon: <BookOutlined />,
    },
    maintenance_requests: {
      name: t('guidelines:categories.maintenance_requests', 'Maintenance Requests'),
      description: t('guidelines:categories.maintenance_requests_desc', 'Procedures and priorities for maintenance requests.'),
      icon: <BookOutlined />,
    },
  };

  // Use mock data instead of API calls
  const { data: guidelineData } = useQuery({
    queryKey: ['guidelines', 'mock'],
    queryFn: async () => {
      const guidelines = await mockGuidelines;
      return { guideline: guidelines[0] || null };
    },
    staleTime: 5 * 60 * 1000,
  });
  const guideline: Guideline | null = guidelineData?.guideline ?? null;
  const records: GuidelineRecord[] = guideline?.records ?? [];

  // Helper function to generate title from category and sub-policy
  const generateRecordTitle = (record: GuidelineRecord): string => {
    if (record.subPolicy) {
      // Handle dynamic amenity sub-policies
      if (record.subPolicy.startsWith('amenity_') && record.amenity) {
        return `${record.amenity.name} Usage Guidelines`;
      }
      // Convert sub-policy to readable title
      return record.subPolicy.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }
    // Fallback to category name
    return CATEGORY_MAP[record.category]?.name || record.category;
  };

  const [isGuidelineModalOpen, setIsGuidelineModalOpen] = useState(false);
  const [editingGuideline, setEditingGuideline] = useState<Guideline | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeKeys, setActiveKeys] = useState<string | string[]>(Object.keys(CATEGORY_MAP));

  const handleAddGuideline = () => {
    setEditingGuideline(null);
    guidelineForm.resetFields();
    setIsGuidelineModalOpen(true);
  };

  const handleEditGuideline = () => {
    if (guideline) {
      setEditingGuideline(guideline);
      setIsGuidelineModalOpen(true);
    }
  };

  const getFilteredRecords = (category: GuidelineCategory) => {
    return records.filter(
      record => record.category === category &&
        (generateRecordTitle(record).toLowerCase().includes(searchTerm.toLowerCase()) ||
         record.content.toLowerCase().includes(searchTerm.toLowerCase()))
    ).sort((a, b) => generateRecordTitle(a).localeCompare(generateRecordTitle(b)));
  };

  const onCollapseChange = (keys: string | string[]) => {
    setActiveKeys(keys);
  };

  // Stub for modal OK handler
  const handleGuidelineModalOk = () => {
    setIsGuidelineModalOpen(false);
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Input
            placeholder={t('searchPlaceholder', 'Search guidelines...')}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ maxWidth: 400 }}
          />
        </Col>
        <Col>
          <Space>
            {guideline ? (
              <Button type="primary" icon={<EditOutlined />} onClick={handleEditGuideline}>
                {t('editGuideline', 'Edit Guidelines')}
              </Button>
            ) : (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddGuideline}>
                {t('addGuideline', 'Add Guidelines')}
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      
      {guideline && (
        <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
          <Text strong>Guideline Version: {guideline.version}</Text>
          <br />
          <Text type="secondary">Status: {guideline.status}</Text>
          <br />
          <Text type="secondary">Effective Date: {dayjs(guideline.effectiveDate).format('MMMM D, YYYY')}</Text>
          {guideline.expiryDate && (
            <>
              <br />
              <Text type="secondary">Expiry Date: {dayjs(guideline.expiryDate).format('MMMM D, YYYY')}</Text>
            </>
          )}
        </div>
      )}
      
      <Collapse activeKey={activeKeys} onChange={onCollapseChange} style={{ marginBottom: 24 }}>
        {Object.entries(CATEGORY_MAP).map(([catKey, cat]) => {
          const filtered = getFilteredRecords(catKey as GuidelineCategory);
          if (searchTerm && filtered.length === 0) return null;
          return (
            <Panel
              header={
                <Space>
                  {cat.icon}
                  <Text strong>{cat.name}</Text>
                  <Text type="secondary">({filtered.length})</Text>
                </Space>
              }
              key={catKey}
              extra={null}
            >
              {cat.description && <Paragraph type="secondary" style={{ marginBottom: 16 }}>{cat.description}</Paragraph>}
              {filtered.length > 0 ? (
                <List
                  itemLayout="vertical"
                  dataSource={filtered}
                  renderItem={item => (
                    <List.Item key={`${item.category}-${item.subPolicy}`}>
                      <List.Item.Meta
                        title={<Text strong>{generateRecordTitle(item)}</Text>}
                        description={
                          <Text type="secondary" style={{ fontSize: '0.8em' }}>
                            {guideline && `Last Updated: ${dayjs(guideline.effectiveDate).format('MMMM D, YYYY')}`}
                          </Text>
                        }
                      />
                      <Paragraph style={{ whiteSpace: 'pre-line' }}>{item.content}</Paragraph>
                    </List.Item>
                  )}
                />
              ) : (
                <Text>{t('noGuidelinesInCategory', 'No guidelines in this category matching your search.')}</Text>
              )}
            </Panel>
          );
        })}
      </Collapse>
      
      {/* Add/Edit Guideline Modal */}
      <GuidelineModal
        open={isGuidelineModalOpen}
        guideline={editingGuideline}
        onSuccess={handleGuidelineModalOk}
        onCancel={() => setIsGuidelineModalOpen(false)}
      />
    </div>
  );
} 